/**
 * CanoneFields — F-003.2 Phase 2 (IMPL-P2-004) + F-005 Phase 5 (IMPL-P5-004).
 *
 * Campi canoneMensile + canoneAnnuale.
 * Mostra un hint advisory (non bloccante) se canoneAnnuale ≠ canoneMensile × 12 ±1%.
 * La validazione cross-field bloccante avviene in validateStep(3) tramite
 * contractFullSchema (non contractDraftSchema che bypassa i refinements).
 *
 * F-005: ring amber + sr-only hint sui campi contestati (`canoneMensile`,
 * `canoneAnnuale` da `BLOCKED_REASON_FIELDS.CONTRATTO_INCOMPLETO`).
 */
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import type { ContractDraft } from '@app/shared';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { useErrorFields } from '../../hooks/useErrorFields';
import {
  CONTESTED_HINT_TEXT,
  CONTESTED_RING_CLASS,
  contestedHintId,
} from './contestedFieldUtils';

export function CanoneFields() {
  const {
    control,
    formState: { errors },
  } = useFormContext<ContractDraft>();

  const canoneMensile = useWatch({ control, name: 'canoneMensile' });
  const canoneAnnuale = useWatch({ control, name: 'canoneAnnuale' });

  const errorFields = useErrorFields();
  const isMensileContested = errorFields.includes('canoneMensile');
  const isAnnualeContested = errorFields.includes('canoneAnnuale');

  // Hint advisory (non blocca) — la validazione reale è in contractFullSchema
  const showCoerenzaHint =
    canoneMensile != null &&
    canoneAnnuale != null &&
    canoneMensile > 0 &&
    canoneAnnuale > 0 &&
    Math.abs(canoneAnnuale - canoneMensile * 12) > Math.max(canoneMensile * 12 * 0.01, 0.01);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Canone mensile */}
        <div className="space-y-1">
          <Label htmlFor="canone-mensile">Canone mensile (€) *</Label>
          <Controller
            name="canoneMensile"
            control={control}
            render={({ field }) => (
              <Input
                id="canone-mensile"
                type="number"
                step="0.01"
                {...field}
                value={field.value ?? ''}
                onChange={(e) =>
                  field.onChange(
                    Number.isNaN(e.target.valueAsNumber) ? undefined : e.target.valueAsNumber,
                  )
                }
                aria-invalid={!!errors.canoneMensile}
                aria-describedby={
                  errors.canoneMensile
                    ? 'canone-mensile-error'
                    : isMensileContested
                      ? contestedHintId('canoneMensile')
                      : undefined
                }
                className={cn(isMensileContested && CONTESTED_RING_CLASS)}
              />
            )}
          />
          {errors.canoneMensile && (
            <p id="canone-mensile-error" className="text-xs text-destructive">
              {errors.canoneMensile.message}
            </p>
          )}
          {isMensileContested && (
            <span id={contestedHintId('canoneMensile')} className="sr-only">
              {CONTESTED_HINT_TEXT}
            </span>
          )}
        </div>

        {/* Canone annuale */}
        <div className="space-y-1">
          <Label htmlFor="canone-annuale">Canone annuale (€) *</Label>
          <Controller
            name="canoneAnnuale"
            control={control}
            render={({ field }) => (
              <Input
                id="canone-annuale"
                type="number"
                step="0.01"
                {...field}
                value={field.value ?? ''}
                onChange={(e) =>
                  field.onChange(
                    Number.isNaN(e.target.valueAsNumber) ? undefined : e.target.valueAsNumber,
                  )
                }
                aria-invalid={!!errors.canoneAnnuale}
                aria-describedby={
                  errors.canoneAnnuale
                    ? 'canone-annuale-error'
                    : isAnnualeContested
                      ? contestedHintId('canoneAnnuale')
                      : undefined
                }
                className={cn(isAnnualeContested && CONTESTED_RING_CLASS)}
              />
            )}
          />
          {errors.canoneAnnuale && (
            <p id="canone-annuale-error" className="text-xs text-destructive">
              {errors.canoneAnnuale.message}
            </p>
          )}
          {isAnnualeContested && (
            <span id={contestedHintId('canoneAnnuale')} className="sr-only">
              {CONTESTED_HINT_TEXT}
            </span>
          )}
        </div>
      </div>

      {/* Hint advisory coerenza canone */}
      {showCoerenzaHint && (
        <p className="text-sm text-amber-600" role="status" aria-live="polite">
          Attenzione: il canone annuale dovrebbe essere ~
          {canoneMensile != null ? (canoneMensile * 12).toFixed(2) : '0'} € (mensile × 12,
          tolleranza ±1%).
        </p>
      )}
    </div>
  );
}
